import * as $ from "jquery";
import * as _ from "lodash";
import { ModuleLayoutCollection } from "../../../../collections/ModuleLayout";
import { ModuleCollection } from "../../../../collections/Modules";
import { PermissionCollection } from "../../../../collections/Permissions";
import { TaskQueue } from "../../../../common/taskQueue";
import { Utils } from "../../../../common/utils";
import { DashboardChannel } from "../../../../models/DashboardChannel";
import { DashboardRange } from "../../../../models/DashboardRange";
import { BaseDashboardWidgetViewOptions } from "../_BaseWidgetView";
import { BaseSimpleWidget } from "../simplewidget/SimpleWidget";

declare var App: any;

// Persists channel selections across widget recreation (tab switches destroy and recreate widgets)
const persistedSelections: { [widgetId: number]: number[] } = {};
let clearListenerRegistered = false;

export class ModuleLayoutWidget extends BaseSimpleWidget {
    ModuleLayoutCollection: ModuleLayoutCollection;
    PermissionCollection: PermissionCollection;
    ModuleCollection: ModuleCollection;

    protected template = _.template(require("templates/dashboard/widgets/layout/layoutmodules.html"));
    protected wrapper = _.template(require("templates/dashboard/widgets/layout/layoutWrapper.html"));
    protected popoverTemplate = _.template(require("templates/dashboard/widgets/layout/modulePopover.html"));
    protected moduleDotTemplate = _.template(require("templates/dashboard/widgets/layout/moduleLayoutWidgetDot.html"));
    protected lastRenderedChannelIds: number[] = [];
    protected channelsSelected: number[] = [];
    private openedPopovers: object[] = [];
    private dotWithChannelsSelected: { [key: string]: number[] } = {};
    private currentFloor;
    private installId;
    private orderedFloors;
    private widgetId;
    private defaultFloor;
    private renderedModules = [];
    private useCustomLabels = true;

    constructor(options: BaseDashboardWidgetViewOptions) {
        super(
            <any>_.merge(
                {
                    tagName: "div",
                    className: "widget module-layout-widget",
                    events: {
                        "click .previous-floor": "handlePrevFloor",
                        "click .next-floor": "handleNextFloor",
                        "click .dragsvg": "handlePopover",
                        "click .popover-channel": "handleChannelClick",
                        "click .w-settings .remove": "handleRemoveWidgetClick",
                        "click .w-cancel": "handleRemoveWidgetCancelClick",
                        "click .w-confirm": "handleRemoveWidgetConfirmClick",
                        "click .w-settings .gear": "handleEditWidgetClick",
                        "click #add-channels-layout-modal": "handleEditWidgetClick",
                    },
                },
                options
            )
        );

        this.$el.attr("data-widget-id", this.options.widget.get("id"));

        // Register a module-level listener once so persistedSelections gets cleared
        // even when all layout widget instances are destroyed (e.g. on a different tab)
        if (!clearListenerRegistered) {
            App.Bus.on('dashboard:clearAllSelections', () => {
                Object.keys(persistedSelections).forEach(key => delete persistedSelections[key]);
            });
            clearListenerRegistered = true;
        }

        this.widgetId = this.options.widget.get("id");
        this.channelsSelected = persistedSelections[this.widgetId] || [];
        this.lastRenderedChannelIds = [];
        const rawUseCustomLabelsConfig = this.options.widget.getParamValue("USE_CUSTOM_LABELS", true);
        // coerce the raw value -- which may be a boolean, number, or string -- into a boolean
        this.useCustomLabels = !!Number(rawUseCustomLabelsConfig);
        this.installId = Number(this.options.widget.getParamValue("LOCATION_ID", ""));
        this.defaultFloor = this.options.widget.getParamValue("DEFAULT_FLOOR", "") || "";
        this.ModuleLayoutCollection = new ModuleLayoutCollection([{ isForWidget: true }], this.installId);
        this.ModuleCollection = new ModuleCollection([{ isForWidget: true }], { installId: this.installId });
        this.PermissionCollection = this.options.permissionCollection;
        this.listenTo(this.options.dashboardChannelCollection, 'sync update', this.changeWidgetCallback.bind(this));
        this.listenTo(this.options.widget, 'change', this.handleWidgetEdit.bind(this));
    }

    private ensureCollectionsLoaded() {
        var outerDeferred = $.Deferred();
        $.when(
            this.ModuleCollection.getCollectionFetchPromise(),
            this.ModuleLayoutCollection.getCollectionFetchPromise()
        ).then(() => {
            outerDeferred.resolve();
        });
        return outerDeferred;
    }

    private ensureValidCurrentFloor(layouts) {
        // If we already have a valid currentFloor, keep it
        if (this.currentFloor && this.isValidFloor(this.currentFloor, layouts)) {
            return;
        }

        // Try to find the user's selected default floor
        if (this.defaultFloor != "") {
            this.currentFloor = _.find(layouts, (layout) => {
                return layout["id"] == Number(this.defaultFloor);
            });
            
            // If the default floor was found and is valid, use it
            if (this.currentFloor && this.isValidFloor(this.currentFloor, layouts)) {
                return;
            }
        }

        // Fallback to the first available layout
        if (layouts.length > 0) {
            this.currentFloor = layouts[0];
            console.log("ModuleLayoutWidget: Using first available layout as current floor");
            return;
        }

        // Last resort: create a fallback for empty layouts
        console.warn("ModuleLayoutWidget: No layouts available for installId", this.installId);
        this.currentFloor = {
            get: function(key) {
                if (key === "id") return "fallback";
                if (key === "name") return "No Layouts Available";
                return null;
            }
        };
    }

    private isValidFloor(floor, layouts) {
        if (!floor) return false;
        return _.some(layouts, (layout) => layout.get("id") === floor.get("id"));
    }

    render() {
        this.renderLayoutList(false);
        $.when(this.ensureCollectionsLoaded())
            .then(() => {
                this.renderLayoutList(true);
            })
            .done(() => {
                $(window).resize(() => {
                    this.resizeWidgetHandler();
                });

                // Trigger window resize without actually resizing to allow widget height change to take effect
                const wSize = this.options.widget.getParamValue("WIDGET_SIZE", "");
                if (wSize === "6") {
                    const el = document;
                    const event = document.createEvent("HTMLEvents");
                    event.initEvent("resize", true, false);
                    el.dispatchEvent(event);
                }
            });

        return this;
    }

    resizeWidgetHandler() {
        var currentFloor = this.currentFloor;
        var widgetId = this.options.widget.get("id");

        // Early return if currentFloor is null or orderedFloors is not available
        if (!currentFloor || !this.orderedFloors) {
            return;
        }

        _.each(this.orderedFloors, (layout) => {
            $("#wd-layout-svg-modules-container-" + layout.get("id") + "-" + widgetId).removeAttr("hidden");
            var coordinates = JSON.parse(layout.get("module_ui_positions"));
            var me = this;

            _.each(coordinates, (coordinate) => {
                var module = me.ModuleCollection.findWhere({ module_id: Number(coordinate.id) });
                if (!module) return;
                var imageId = "#wd-img-modules-container-" + layout.get("id") + "-" + widgetId;
                var resizeCoordinates = this.getScreenWidth(coordinate, imageId);

                $("#svg-drag-module-" + module.get("module_id") + "-" + widgetId).css({
                    width: resizeCoordinates.width,
                    height: resizeCoordinates.height,
                    left: resizeCoordinates.left,
                    top: resizeCoordinates.top,
                });
            });

            if (currentFloor.get("id") != layout.get("id")) {
                $("#wd-layout-svg-modules-container-" + layout.get("id") + "-" + widgetId).attr("hidden", "");
            }
        });
    }

    changeWidgetCallback() {
        // Early return if widget is not in a valid state
        if (!this.currentFloor || !this.orderedFloors || !this.ModuleLayoutCollection) {
            console.warn("ModuleLayoutWidget: changeWidgetCallback called before widget is properly initialized");
            return;
        }

        var elem = this.$el;
        $(elem).removeClass(function (index, className) {
            return (className.match(/(^|\s)widget-w-\S+/g) || []).join(" ");
        });
        elem.addClass("widget-w-" + this.options.widget.getParamValue("WIDGET_SIZE", "6"));
        this.resizeWidgetHandler();

        var layouts = _.sortBy(this.ModuleLayoutCollection.where({ install_id: this.installId }), (layout) => {
            return layout.get("order");
        });
        
        // Ensure we have a valid current floor
        this.ensureValidCurrentFloor(layouts);
        
        // Update the floor display
        $("#floor-" + this.widgetId).empty();
        $("#floor-" + this.widgetId).text(this.currentFloor.get("name"));
        
        // Just update the existing modules without closing popovers
        if (this.currentFloor) {
            this.renderLayoutModules(this.currentFloor);
        }
    }

    private handleWidgetEdit() {
        // Complete re-render from scratch when widget is edited
        this.clearPopovers();
        this.$el.empty();
        
        // Reset all state
        this.renderedModules = [];
        this.currentFloor = null;
        this.orderedFloors = null;
        this.installId = Number(this.options.widget.getParamValue("LOCATION_ID", ""));
        this.defaultFloor = this.options.widget.getParamValue("DEFAULT_FLOOR", "") || "";
        
        // Recreate collections with new install ID
        this.ModuleLayoutCollection = new ModuleLayoutCollection([{ isForWidget: true }], this.installId);
        this.ModuleCollection = new ModuleCollection([{ isForWidget: true }], { installId: this.installId });
        
        // Re-render from scratch
        this.render();
        
        // Reinitialize drag functionality
        setTimeout(() => {
            if (this.options.parentView && (this.options.parentView as any).layout) {
                (this.options.parentView as any).layout.layout(true);
            }
        }, 100);
    }

    renderLayoutList(collectionLoaded) {
        if (collectionLoaded) {
            var layouts = _.sortBy(this.ModuleLayoutCollection.where({ install_id: this.installId }), (layout) => {
                return layout.get("order");
            });

            // Always ensure we have a valid currentFloor
            this.ensureValidCurrentFloor(layouts);
            this.orderedFloors = layouts;

            this.$(".w-rows").html(
                this.template({
                    widgetTitle: this.options.widget.getParamValue("LAYOUT_WIDGET_TITLE", ""),
                    currentFloor: this.currentFloor.get("name"),
                    layoutCollection: this.ModuleLayoutCollection,
                    moduleCollection: this.ModuleCollection,
                    widgetId: this.widgetId,
                    collectionLoaded: collectionLoaded,
                    currentInstallId: this.installId,
                    dashboardId: this.options.widget.get("dashboard_id"),
                    permissionCollection: this.options.permissionCollection,
                    editable: this.options.editable,
                    movable: this.options.movable,
                    cloneable: this.options.cloneable,
                    deletable: this.options.deletable,
                })
            );

            $(this.$el).removeClass(function (index, className) {
                return (className.match(/(^|\s)widget-w-\S+/g) || []).join(" ");
            });
            // Make some time so that the image gets loaded before rendering the modules
            // TODO: trigger this off of an event rather than a magic timeout
            setTimeout(() => {
                this.renderModules(collectionLoaded, false);
            }, 1000);
        } else {
            this.$el.empty();
            this.$el.html(
                this.wrapper({
                    layoutCollection: this.ModuleLayoutCollection,
                    moduleCollection: this.ModuleCollection,
                    widgetId: this.widgetId,
                    collectionLoaded: collectionLoaded,
                    currentInstallId: this.installId,
                    dashboardId: this.options.widget.get("dashboard_id"),
                    permissionCollection: this.options.permissionCollection,
                    editable: this.options.editable,
                    movable: this.options.movable,
                    cloneable: this.options.cloneable,
                    deletable: this.options.deletable,
                })
            );
        }
        this.$el.addClass("widget-w-" + this.options.widget.getParamValue("WIDGET_SIZE", "6"));
        // this.$el.addClass('widget-h-3');
    }

    handlePrevFloor() {
        this.clearPopovers();
        var currentFloor = this.currentFloor;
        var widgetId = this.options.widget.get("id");
        var widgetTitle = this.options.widget.getParamValue("LAYOUT_WIDGET_TITLE", "");

        // Early return if currentFloor is null or orderedFloors is not available
        if (!currentFloor || !this.orderedFloors) {
            return;
        }

        // Get index of current floor
        var index = _.findIndex(this.orderedFloors, { id: currentFloor.get("id") });
        // Show new selected floor and change title
        if (index > 0) {
            // Hide currentFloor
            $("#wd-layout-svg-modules-container-" + currentFloor.get("id") + "-" + widgetId).attr("hidden", "");

            //Show new currentFloor
            this.currentFloor = this.orderedFloors[index - 1];
            $("#wd-layout-svg-modules-container-" + this.currentFloor.get("id") + "-" + widgetId).removeAttr("hidden");

            this.renderLayoutModules(this.currentFloor);
            $("#floor-" + widgetId).text(this.currentFloor.get("name"));
            $("#title-" + widgetId).text(widgetTitle);
        }
    }

    handleNextFloor() {
        this.clearPopovers();
        var currentFloor = this.currentFloor;
        var widgetId = this.options.widget.get("id");
        var widgetTitle = this.options.widget.getParamValue("LAYOUT_WIDGET_TITLE", "");

        // Early return if currentFloor is null or orderedFloors is not available
        if (!currentFloor || !this.orderedFloors) {
            return;
        }

        // Get index of current floor
        var index = _.findIndex(this.orderedFloors, { id: currentFloor.get("id") });
        // Show new selected floor and change title
        if (index < this.orderedFloors.length - 1) {
            // Hide currentFloor
            $("#wd-layout-svg-modules-container-" + currentFloor.get("id") + "-" + widgetId).attr("hidden", "");

            //Show new currentFloor
            this.currentFloor = this.orderedFloors[index + 1];
            $("#wd-layout-svg-modules-container-" + this.currentFloor.get("id") + "-" + widgetId).removeAttr("hidden");

            this.renderLayoutModules(this.currentFloor);
            $("#floor-" + widgetId).text(this.currentFloor.get("name"));
            $("#title-" + widgetId).text(widgetTitle);
        }
    }

    show() {
        this.render();
    }

    // Alternative function that could be used to render each floor image separately (e.g. when the user changes floor)
    // renderLayoutImage(layout) {
    //     var widgetId = this.options.widget.get('id');
    //     var template = `<div class="floor-${widgetId}" id="wd-layout-svg-modules-container-${layout.get('id')}-${widgetId}"
    //                     data-layout-id="${layout.get('id')}" style="overflow:hidden;right: 0;" hidden>
    //                         <img class="layout-img" style="max-width:100%" id="wd-img-modules-container-${layout.get('id')}-${widgetId}"
    //                         src="${layout.get('layout_image')}"
    //                         alt="Layout image">
    //                     </div>`;
    //     var isFloorRendered = $('#wd-layout-svg-modules-container-' + layout.get('id') + '-' + widgetId);
    //     if (isFloorRendered.length == 0) $('#wd-layout-svg-container').append(template);
    // }

    private getIAQLikeSensorTypes() {
        // IAQ like sensor types, for example the internal sensor, deprecated environmental sensor
        return [
            34, // IAQ Sensor
            30, // Environmental Sensor (deprecated),
            1   // Internal Sensor
        ];
    }

    private getLighterColor(hexColor) {
        try {
            // Check if it's a valid hex color (3, 4, 6, or 8 characters after #)
            if (!hexColor || typeof hexColor !== 'string' || !hexColor.match(/^#[0-9A-Fa-f]{3,8}$/)) {
                return '#F1F5F9'; // Return default for non-hex colors like 'grey'
            }
            
            // Convert hex to RGB
            const hex = hexColor.replace('#', '');
            const r = parseInt(hex.substr(0, 2), 16);
            const g = parseInt(hex.substr(2, 2), 16);
            const b = parseInt(hex.substr(4, 2), 16);
            
            // Mix with white (80% white, 80% original color)
            const lighterR = Math.round(r * 0.5 + 255 * 0.5);
            const lighterG = Math.round(g * 0.5 + 255 * 0.5);
            const lighterB = Math.round(b * 0.5 + 255 * 0.5);
            
            // Convert back to hex
            return `#${lighterR.toString(16).padStart(2, '0')}${lighterG.toString(16).padStart(2, '0')}${lighterB.toString(16).padStart(2, '0')}`;
        } catch (error) {
            // Fallback to default light gray if color conversion fails
            return '#F1F5F9';
        }
    }

    private getNodeStyling(colorStatus, hasChannels = true) {
        return {
            background: hasChannels ? this.getLighterColor(colorStatus) : '#eaeaea',
            transform: 'scale(1)',
            borderColor: colorStatus,
            iconColor: ''
        };
    }

    renderLayoutModules(layout) {
        var widgetId = this.options.widget.get("id");
        var allChannels = this.options.dashboardChannelCollection.filter((dashboardChannel) => {
            return dashboardChannel.get("dashboard_widget_id") == this.options.widgetId;
        });

        var floorId = "#wd-layout-svg-modules-container-" + layout.get("id") + "-" + widgetId;
        var imageId = "#wd-img-modules-container-" + layout.get("id") + "-" + widgetId;
        $("#wd-layout-svg-modules-container-" + layout.get("id") + "-" + widgetId).removeAttr("hidden");
        
        var coordinates = JSON.parse(layout.get("module_ui_positions"));

        // Render the modules in groups of x (modulesBatchSize)
        const self = this;
        const modulesBatchSize = 5;

        // Create array with groups of modules based on modulesBatchSize
        const moduleCoordinatesGroups = [];
        for (let i = 0; i < coordinates.length; i += modulesBatchSize) {
            moduleCoordinatesGroups.push(coordinates.slice(i, i + modulesBatchSize));
        }

        // Create task for each group of modules
        for (let i = 0; i < moduleCoordinatesGroups.length; i++) {
            TaskQueue.shared.enqueue(function renderGroupOfModules() {
                _.each(moduleCoordinatesGroups[i], (coordinate) => {
                    var module = self.ModuleCollection.findWhere({ module_id: Number(coordinate.id) });
                    if (!module) return;

                    var moduleId = module.get("module_id");
                    var channels = allChannels.filter((dashboardChannel) => {
                        return dashboardChannel.get("module_id") == moduleId;
                    });

                    var colorStatus = self.getModuleColorStatus(channels);
                    var resizeCoordinates = self.getScreenWidth(coordinate, imageId);
                    var lastSeen = channels.length
                        ? Utils.Time.getChannelLastSeenString(_.head(channels).get("channel_last_timestamp"))
                        : null;

                    // Check if any channel is offline (stale or has no data) to determine if we should show the offline icon
                    var hasOfflineChannels = false;
                    if (channels.length > 0) {
                        var anyChannelStale = _.some(channels, (channel) => {
                            return Utils.Time.isChannelLastSeenStale(channel.get("channel_last_timestamp"));
                        });
                        var anyChannelNoData = _.some(channels, (channel) => {
                            var channelLastSeen = Utils.Time.getChannelLastSeenString(channel.get("channel_last_timestamp"));
                            return channelLastSeen === "No Data" || channelLastSeen === null;
                        });
                        hasOfflineChannels = anyChannelStale || anyChannelNoData;
                    }

                    // Determine node type and styling
                    var nodeStyling = self.getNodeStyling(colorStatus, channels.length > 0);

                    // Check if module is already rendered to prevent duplicates
                    if (!self.renderedModules.includes(moduleId)) {
                        $(floorId).append(
                            self.moduleDotTemplate({
                                module: module,
                                resizeCoordinates: resizeCoordinates,
                                colorStatus: colorStatus,
                                widgetId: widgetId,
                                lastSeen: lastSeen,
                                nodeStyling: nodeStyling,
                                hasOfflineChannels: hasOfflineChannels,
                                desktopMultiplier: self.getDesktopMultiplier()
                            })
                        );

                        self.renderDataPopover(module, channels);
                        self.renderedModules.push(moduleId);
                    } else {
                        // Check if this module has an open popup before updating
                        var hasOpenPopup = _.find(self.openedPopovers, { moduleId: moduleId, widgetId: widgetId });
                        
                        // Close existing popover if it's open to prevent duplication
                        if (hasOpenPopup) {
                            (<any>$(' [data-toggle="' + moduleId + "-" + widgetId + '-popover"]')).popover("hide");
                        }
                        
                        // Remove existing popover before updating to prevent duplication
                        $(`.module-popover-${widgetId}[data-module-id="${moduleId}"]`).remove();
                        
                        // Update the entire module dot with fresh data
                        $(`#svg-drag-module-${moduleId}-${widgetId}`).replaceWith(
                            self.moduleDotTemplate({
                                module: module,
                                resizeCoordinates: resizeCoordinates,
                                colorStatus: colorStatus,
                                widgetId: widgetId,
                                lastSeen: lastSeen,
                                nodeStyling: nodeStyling,
                                hasOfflineChannels: hasOfflineChannels,
                                desktopMultiplier: self.getDesktopMultiplier()
                            })
                        );

                        self.renderDataPopover(module, channels);
                    }
                });
            });
        }
    }

    renderModules(collectionLoaded, loadPopovers) {
        if (!collectionLoaded) return;

        var widgetId = this.options.widget.get("id");

        // Only proceed if currentFloor is available
        if (!this.currentFloor) {
            return;
        }

        this.renderLayoutModules(this.currentFloor);

        $("#floor-" + widgetId).text(this.currentFloor.get("name"));
        $("#title-" + widgetId).text(this.options.widget.getParamValue("LAYOUT_WIDGET_TITLE", ""));
        if (loadPopovers) {
            // Load popovers
            _.each(this.openedPopovers, (ids) => {
                (<any>$(' [data-toggle="' + ids["moduleId"] + "-" + ids["widgetId"] + '-popover"]')).popover("show");
            });
        }
        
        // Trigger layout refresh to prevent overlapping with widgets below
        window['App'].Bus.trigger('dashboard:category:' + this.options.categoryId + ':layout');
    }

    clearPopovers() {
        _.each(this.openedPopovers, (ids) => {
            (<any>$(' [data-toggle="' + ids["moduleId"] + "-" + ids["widgetId"] + '-popover"]')).popover("hide");
            Boolean($("#svg-drag-module-" + ids["moduleId"] + "-" + ids["widgetId"]).data("info", false));
        });
        this.openedPopovers = [];
    }

    private getDesktopMultiplier(): number {
        var widgetSize = this.options.widget.getParamValue("WIDGET_SIZE", "6");
        switch (widgetSize) {
            case "2": return 1.0;  // Small widget
            case "3": return 1.1; // Medium widget
            case "4": return 1.25;  // Large widget
            case "6": return 1.35;  // Full screen
            default: return 1.0;
        }
    }

    getScreenWidth(coordinate, container) {
        // The container parameter is actually the image element itself
        var $image = $(container);
        var $parentContainer = $image.parent();
        var containerHeight = $parentContainer.height();
        var containerWidth = $parentContainer.width();
        
        // Get the actual image dimensions and position after CSS is applied
        var imageWidth = $image.width();
        var imageHeight = $image.height();
        var imageOffset = $image.offset();
        var containerOffset = $parentContainer.offset();
        
        // Fixed base dot size for consistency across all layout widgets
        var fixedDotSize = 20; // Base size in pixels
        
        // Fallback to original calculation if image isn't ready
        if (!imageWidth || !imageHeight || !imageOffset || !containerOffset) {
            // Calculate the image's actual displayed dimensions
            var imageAspectRatio = coordinate.widthImg / coordinate.heightImg;
            var containerAspectRatio = containerWidth / containerHeight;
            
            var displayedImageWidth, displayedImageHeight, offsetX = 0, offsetY = 0;
            
            if (imageAspectRatio > containerAspectRatio) {
                // Image is wider than container - fit to width
                displayedImageWidth = containerWidth;
                displayedImageHeight = containerWidth / imageAspectRatio;
                offsetY = (containerHeight - displayedImageHeight) / 2; // Center vertically
            } else {
                // Image is taller than container - fit to height
                displayedImageHeight = containerHeight;
                displayedImageWidth = containerHeight * imageAspectRatio;
                offsetX = (containerWidth - displayedImageWidth) / 2; // Center horizontally
            }
            
            // Calculate dot positions relative to the displayed image
            var containerH = (displayedImageHeight * coordinate.y) / coordinate.heightImg + offsetY;
            var containerW = (displayedImageWidth * coordinate.x) / coordinate.widthImg + offsetX;
            // Use fixed dot size instead of image-relative sizing
            var moduleH = fixedDotSize;
            var moduleW = fixedDotSize;

            return { left: containerW, top: containerH, width: moduleW, height: moduleH };
        }
        
        // Calculate the image's position relative to the parent container
        var imageLeft = imageOffset.left - containerOffset.left;
        var imageTop = imageOffset.top - containerOffset.top;
        
        // Calculate dot positions relative to the actual positioned image
        var containerH = (imageHeight * coordinate.y) / coordinate.heightImg + imageTop;
        var containerW = (imageWidth * coordinate.x) / coordinate.widthImg + imageLeft;
        // Use fixed dot size instead of image-relative sizing
        var moduleH = fixedDotSize;
        var moduleW = fixedDotSize;

        return { left: containerW, top: containerH, width: moduleW, height: moduleH };
    }

    private getModuleColorStatus(channels) {
        if (!channels || channels.length === 0) {
            return "#bebebe"; // Light gray border for no channels
        }

        // Check if ALL channels are offline
        var allChannelsStale = _.every(channels, (channel) => {
            return Utils.Time.isChannelLastSeenStale(channel.get("channel_last_timestamp"));
        });
        var allChannelsNoData = _.every(channels, (channel) => {
            var channelLastSeen = Utils.Time.getChannelLastSeenString(channel.get("channel_last_timestamp"));
            return channelLastSeen === "No Data" || channelLastSeen === null;
        });
        
        // If all channels are offline, return gray
        if (allChannelsStale || allChannelsNoData) {
            return "#94A3B8";
        }

        // Otherwise, get the threshold color from the furthest range
        var furthestRange = null;
        var furthestRangeIndexSum = 0;
        // Get furthest range from default "OK" range of every channel
        _.each(channels, (dchannel: DashboardChannel) => {
            var activeRange = dchannel.getRange();
            var idxActiveRange, idxDefaultRange;
            _.sortBy(dchannel.get("rangesList"), (r) => r.max).forEach((range, idx) => {
                if (!(range instanceof DashboardRange)) range = new DashboardRange(range);
                if (range.get("is_default")) {
                    idxDefaultRange = idx;
                } else if (activeRange.get("id") == range.get("id")) {
                    idxActiveRange = idx;
                }
            });

            if (idxDefaultRange + idxActiveRange > furthestRangeIndexSum) {
                furthestRange = activeRange;
                furthestRangeIndexSum = idxDefaultRange + idxActiveRange;
            }
        });

        // Use color from the range that is furthest away
        if (furthestRange) return furthestRange.get("color");
        return Utils.colors.green;
    }

    renderDataPopover(moduleModel, channels) {
        var widgetId = this.options.widgetId;
        var lastSeen = null;
        var lastSeenColor = null;
        var moduleId = moduleModel.get("module_id");

        _.each(channels, (dchannel: DashboardChannel) => {
            var lastSeenStr = Utils.Time.getChannelLastSeenString(dchannel.get("channel_last_timestamp"));
            var lastSeenColorStr = Utils.Time.getChannelLastSeenColor(dchannel.get("channel_last_timestamp"));
            var channelLastSeenOpacity = Utils.Time.getChannelLastSeenOpacity(dchannel.get("channel_last_timestamp"));
            if (lastSeen == null) {
                lastSeen = lastSeenStr;
                lastSeenColor = lastSeenColorStr;
            }
            var activeRange = dchannel.getRange();

            var arrow = null;
            if (activeRange.get("description").toLowerCase() == "value below threshold") {
                arrow = "down";
            } else if (activeRange.get("description").toLowerCase() == "value above threshold") {
                arrow = "up";
            }
            var id = dchannel.get("dashboard_channel_id");
            var clicked = _.includes(this.channelsSelected, id);

            let channelLabel = dchannel.get("dashboard_channel_name");
            if (!this.useCustomLabels)
                channelLabel = `${dchannel.get("module_location")} - ${dchannel.get("channel_name")}`;

            dchannel.set({
                label: channelLabel,
                state: activeRange.get("name"),
                lastSeen: lastSeenStr,
                lastSeenColor: lastSeenColorStr,
                channelLastSeenOpacity: channelLastSeenOpacity,
                clicked: clicked,
                arrow: arrow,
                color_status: activeRange.get("color"),
                icon: this.getIcon(dchannel),
            });
        });

        var popoverTemplate = [
            '<div class="popover module-popover-' +
                widgetId +
                '" id="module-popover-' +
                moduleModel.get("module_id") +
                "-" +
                widgetId +
                '" style="padding: 10px 6px; margin-top: 12px; border-radius: 6px;' +
                'border: 1px solid #d1d5db;">',
            '<div class="arrow"></div>',
            '<div class="popover-content" style="padding-top:4px">',
            "</div>",
            "</div>",
        ].join("");

        var content = $(
            <string>this.popoverTemplate({
                channels: channels,
                moduleModel: moduleModel,
                widgetId: widgetId,
                lastSeen: lastSeen,
                lastSeenColor: lastSeenColor,
                permissionCollection: this.options.permissionCollection,
                currentInstallId: this.installId,
                Utils: Utils,
            })
        );

        var dot = "#svg-drag-module-" + moduleId + "-" + widgetId;

        // To keep the shadow even after it refreshes the widget
        if (!this.dotWithChannelsSelected[moduleId]) {
            // Rebuild from persisted channelsSelected for this module's channels
            var selectedForModule = _.filter(channels, (ch) => {
                return _.includes(this.channelsSelected, ch.get("dashboard_channel_id"));
            });
            this.dotWithChannelsSelected[moduleId] = _.map(selectedForModule, (ch) => ch.get("dashboard_channel_id"));
        }
        if (this.dotWithChannelsSelected[moduleId].length) {
            $(dot).css("box-shadow", " 0px 0px 20px 5px " + $(dot).css("background-color"));
        }

        // Destroy existing popover before creating new one to prevent duplicates
        (<any>$('[data-toggle="' + moduleId + "-" + widgetId + '-popover"]')).popover('destroy');
        
        // Remove any existing popover DOM elements
        $(`.module-popover-${widgetId}[data-module-id="${moduleId}"]`).remove();

        (<any>$('[data-toggle="' + moduleId + "-" + widgetId + '-popover"]')).popover({
            selector: "[rel=popover]",
            trigger: "click",
            content: content,
            template: popoverTemplate,
            placement: "bottom",
            html: true,
        });

        // Show popover if it was open (but don't show immediately to avoid duplication)
        var isPopoverOpened = this.openedPopovers.find((popover) => popover["moduleId"] === moduleId);
        if (isPopoverOpened) {
            // Use a small delay to ensure the new popover is properly initialized
            setTimeout(() => {
                (<any>$(' [data-toggle="' + moduleId + "-" + widgetId + '-popover"]')).popover("show");
                Boolean($("#svg-drag-module-" + moduleId + "-" + widgetId).data("info", true));
                // Re-apply clicked channel colors after popover is rendered
                // Use longer delay to ensure Bootstrap has fully rendered the popover
                this.restoreClickedChannelColors(moduleId, widgetId);
            }, 50);
        } else {
            // Also restore colors if popover is newly created (in case channels were already selected)
            setTimeout(() => {
                this.restoreClickedChannelColors(moduleId, widgetId);
            }, 50);
        }
    }

    restoreClickedChannelColors(moduleId: any, widgetId: any, retryCount: number = 0) {
        // Bootstrap popovers are appended to body — find the popover and re-apply clicked class
        var popoverId = "module-popover-" + moduleId + "-" + widgetId;
        var popover = $("#" + popoverId);

        if (!popover.length) {
            popover = $(".module-popover-" + widgetId).filter((index, el) => {
                return $(el).attr("id") === popoverId;
            });
        }

        if (!popover.length) {
            if (retryCount < 5) {
                setTimeout(() => {
                    this.restoreClickedChannelColors(moduleId, widgetId, retryCount + 1);
                }, 50 * (retryCount + 1));
            }
            return;
        }

        _.each(this.channelsSelected, (channelId) => {
            var channelRow = popover.find('[data-channel-id="' + channelId + '"]');
            if (!channelRow.length) {
                channelRow = popover.find('[data-channel-id]').filter((index, el) => {
                    return Number($(el).attr('data-channel-id')) === Number(channelId);
                });
            }
            if (channelRow.length) {
                channelRow.addClass("clicked");
            }
        });
    }

    handlePopover(e: JQuery.UIEventBase) {
        var item = e.currentTarget;
        var widgetId = this.options.widget.get("id");
        var moduleId = $(e.currentTarget).data("module-id");
        var visible = _.find(this.openedPopovers, { moduleId: moduleId, widgetId: widgetId });
        
        if (visible) {
            // If this popup is already visible, close it
            (<any>$(' [data-toggle="' + moduleId + "-" + widgetId + '-popover"]')).popover("hide");
            this.openedPopovers = _.remove(this.openedPopovers, (op) => {
                return !(op["moduleId"] == moduleId && op["widgetId"] == widgetId);
            });
            Boolean($("#svg-drag-module-" + moduleId + "-" + widgetId).data("info", false));
        } else {
            // Close all other popups first
            this.clearPopovers();
            
            // Then open the new popup
            (<any>$(' [data-toggle="' + moduleId + "-" + widgetId + '-popover"]')).popover("show");
            this.openedPopovers.push({ moduleId, widgetId });
            Boolean($("#svg-drag-module-" + moduleId + "-" + widgetId).data("info", true));
            // Re-apply clicked channel colors after popover is shown
            setTimeout(() => {
                this.restoreClickedChannelColors(moduleId, widgetId);
            }, 10);
        }
    }

    handleChannelClick(e: JQuery.UIEventBase) {
        var elem = <any>$(e.currentTarget);
        var dotId = $(elem).attr("id");
        var dot = "#svg-drag-module-" + dotId;
        dotId = dotId.substring(0, dotId.indexOf("-"));
        var channelId = Number(elem.attr("data-channel-id"));

        if (_.includes(this.channelsSelected, channelId)) {
            this.channelsSelected = _.without(this.channelsSelected, channelId);
            this.dotWithChannelsSelected[dotId] = _.without(this.dotWithChannelsSelected[dotId], channelId);
            elem.removeClass("clicked");
            this.sendChannelDeselectSignal(channelId);
            if (!this.dotWithChannelsSelected[dotId].length) $(dot).css("box-shadow", "");
        } else {
            this.channelsSelected.push(channelId);
            elem.addClass("clicked");
            this.sendChannelSelectSignal(channelId);
            $(dot).css("box-shadow", " 0px 0px 20px 5px " + $(dot).css("background-color"));
            this.dotWithChannelsSelected[dotId].push(channelId);
        }
        persistedSelections[this.widgetId] = [...this.channelsSelected];
    }

    resendAllSelectedChannelSignals() {
        _.each(_.intersection(this.lastRenderedChannelIds, this.channelsSelected), (id) => {
            this.sendChannelSelectSignal(id);
        });
    }

    sendChannelSelectSignal(channelId) {
        App.Bus.trigger("dashboard:category:" + this.options.categoryId + ":chart:addChannel", channelId);
        App.Bus.trigger("dashboard:category:all:chart:addChannel", channelId);
    }

    sendChannelDeselectSignal(channelId) {
        App.Bus.trigger("dashboard:category:" + this.options.categoryId + ":chart:removeChannel", channelId);
        App.Bus.trigger("dashboard:category:all:chart:removeChannel", channelId);
    }

    protected clearAllSelections() {
        // Remove dot shadows
        _.each(this.dotWithChannelsSelected, (channels, dotId) => {
            if (channels.length) {
                $(`#svg-drag-module-${dotId}-${this.widgetId}`).css("box-shadow", "");
            }
        });
        this.dotWithChannelsSelected = {};

        // Remove clicked class from popover channels (popovers are in <body>, outside this.$el)
        $(`.module-popover-${this.widgetId} .popover-channel`).removeClass("clicked");

        delete persistedSelections[this.widgetId];
        super.clearAllSelections();

        // Re-render popover content so cached Bootstrap popover HTML no longer has "clicked" class
        if (this.currentFloor) {
            this.renderLayoutModules(this.currentFloor);
        }
    }

    renderChannels() {
        //Required method for BaseSimpleWidget
    }

    private getIcon(channel) {
        var type = channel.get('sensor_type');
        var pos = channel.get('channel_position');
        var version = channel.get('sensor_version');
        var _default = 'fa-regular fa-gauge';

        switch (type) {
            case 1:
                if (version <= 4) {
                    switch (pos) {
                        case 1:
                            // Voltage
                            return "fa-regular fa-bolt";
                        case 2:
                            // Temp
                            return "fa-regular fa-temperature-three-quarters";
                        case 3:
                            // Humidity
                            return "fa-regular fa-water";
                    }
                } else if (version >= 5) {
                    switch (pos) {
                        case 1:
                            // Temp
                            return "fa-regular fa-temperature-three-quarters";
                        case 2:
                            // Humidity
                            return "fa-regular fa-water";
                        case 3:
                            // Voltage
                            return "fa-regular fa-bolt";
                    }
                }
                return _default;
            case 10:
                // Pulse Power
                return "fa-regular fa-bolt";
            case 11:
                return "fa-regular fa-bolt";
            case 12:
                switch (pos) {
                    // Temp
                    case 1:
                        return "fa-regular fa-temperature-three-quarters";
                    // Hum
                    case 2:
                        return "fa-regular fa-water";
                    // CO2
                    case 3:
                        return "fa-regular fa-wind";
                }
                return _default;
            case 14:
                // Light exposure
                return "fa-regular fa-lightbulb";
            case 15:
                // Motion
                return "fa-regular fa-person-walking";
            case 16:
                // Door
                return "fa-regular fa-door-open";
            case 17:
                // Water leak
                return "fa-regular fa-droplet";
            case 18:
                // Smoke
                return "fa-regular fa-smoke";
            case 19:
                // Glass break
                return "fa-regular fa-shield-halved";
            case 20:
                // Vibration
                return "fa-regular fa-shake";
            case 21:
                // Sound
                return "fa-regular fa-volume-high";
            case 22:
                // Pressure
                return "fa-regular fa-gauge";
            case 23:
                // Flow
                return "fa-regular fa-water";
            case 24:
                // Level
                return "fa-regular fa-chart-line";
            case 25:
                // Current
                return "fa-regular fa-bolt";
            case 26:
                // Power
                return "fa-regular fa-bolt";
            case 27:
                // Energy
                return "fa-regular fa-bolt";
            case 28:
                // Gas
                return "fa-regular fa-wind";
            case 29:
                // Air quality
                return "fa-regular fa-wind";
            case 30:
                // Environmental (deprecated)
                switch (pos) {
                    case 1:
                        return "fa-regular fa-temperature-three-quarters";
                    case 2:
                        return "fa-regular fa-water";
                    case 3:
                        return "fa-regular fa-wind";
                    case 4:
                        return "fa-regular fa-wind";
                }
                return _default;
            case 31:
                // Relay
                return "fa-regular fa-toggle-on";
            case 32:
                // Switch
                return "fa-regular fa-toggle-on";
            case 33:
                // Dimmer
                return "fa-regular fa-lightbulb";
            case 34:
                // IAQ Sensor
                switch (pos) {
                    case 1:
                        return "fa-regular fa-temperature-three-quarters";
                    case 2:
                        return "fa-regular fa-water";
                    case 3:
                        return "fa-regular fa-wind";
                    case 4:
                        return "fa-regular fa-wind";
                    case 7:
                        return "fa-regular fa-wind";
                    case 8:
                        return "fa-regular fa-wind";
                    case 99:
                        return "fa-regular fa-wind";
                    case 100:
                        return "fa-regular fa-wind";
                    case 103:
                        return "fa-regular fa-wind";
                    case 106:
                        return "fa-regular fa-wind";
                    case 109:
                        return "fa-regular fa-wind";
                    case 112:
                        return "fa-regular fa-wind";
                }
                return _default;
            case 35:
                // Occupancy
                return "fa-regular fa-person-walking";
            case 36:
                // Contact
                return "fa-regular fa-door-open";
            case 37:
                // Tamper
                return "fa-regular fa-shield-halved";
            case 38:
                // Battery
                return "fa-regular fa-battery-half";
            case 39:
                // Signal strength
                return "fa-regular fa-signal";
            case 40:
                // Network
                return "fa-regular fa-wifi";
            default:
                return _default;
        }
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/dashboard/widgets/layout/ModuleLayoutWidget.ts